import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { getAccessByCode, requestCodeGeneration } from "./getAccess";
import { parseAuthError } from "./authSignInErrors";

type Props = {
  open: boolean;
  onSuccess?: () => any;
  onClose?: () => any;
};

export const PhoneSignDialog: FC<Props> = ({
  open,
  onSuccess = () => {},
  onClose = () => {},
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");

  const [hasSentRequest, setHasSentRequest] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lockSeconds, setLockSeconds] = useState(0);

  // Countdown while the account is throttled / in a grace period.
  useEffect(() => {
    if (lockSeconds <= 0) return;
    const timer = setInterval(() => setLockSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [lockSeconds]);

  const requestCode = () => {
    requestCodeGeneration(phoneNumber)
      .then(() => {
        setHasSentRequest(true);
        setCode("");
        setErrorMessage("");
      })
      .catch((e) => {
        const info = parseAuthError(e);
        if (info.status === 429) {
          if (info.lockedUntilSeconds) setLockSeconds(info.lockedUntilSeconds);
          setErrorMessage("Too many code requests. Please wait before trying again.");
        } else {
          setErrorMessage("Something went wrong, please check the number you have entered.");
        }
      });
  };

  const submitCode = () => {
    getAccessByCode(phoneNumber, code)
      .then(() => {
        setErrorMessage("");
        onSuccess();
      })
      .catch((e) => {
        const info = parseAuthError(e);
        setErrorMessage(info.message);
        if (info.lockedUntilSeconds) setLockSeconds(info.lockedUntilSeconds);
        if (info.mustRequestNewCode) {
          setHasSentRequest(false);
          setCode("");
        }
      });
  };

  const handleSend = () => {
    if (lockSeconds > 0) return;
    if (hasSentRequest) submitCode();
    else requestCode();
  };

  const buttonLabel = lockSeconds > 0
    ? `Wait ${lockSeconds}s`
    : hasSentRequest ? "Sign In" : "Send";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>Sign In</Box>
          <Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {hasSentRequest ? (
          <TextField
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        ) : (
          <TextField
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        )}
        <br/>
        <span style={{ color: 'red' }}>{errorMessage}</span>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSend} disabled={lockSeconds > 0}>{buttonLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};
